import DatePicker from "react-datepicker";
import moment from "moment-timezone";
import { useTranslation } from "react-i18next";
import useClientConfig from "../hooks/clientConfigHook";
import { FloatingLabel } from "./FloatingLabel";
import PropTypes from 'prop-types';
import Label from "./Label";
import { forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";
import { Validator } from "./ValidationResult";
import { FormControl } from "react-bootstrap";
import { makeLabel } from "../utils/generalUtils";
import { cdate, parseCustomFieldDateToISO } from "../utils/CDate";
import { withComponentCapabilities } from "./capabilities/componentCapabilities";
import { materializeValue } from "../utils/lazyValue";
import { parseDate } from "../utils/dateUtils";
import { Col, Row } from "./Grid";

const QueryControl = ({ def, value, minDate, maxDate, tz, href, onChange, placeholder, args, ...props }) => {
    const { t } = useTranslation();
    const clientConfig = useClientConfig();
    const startValue = parseCustomFieldDateToISO(value?.start, args?.tz ?? def?.tz ?? clientConfig?.tz);
    const endValue = parseCustomFieldDateToISO(value?.end, args?.tz ?? def?.tz ?? clientConfig?.tz);

    return <Row>
        <Col md><TimeInput tz={args?.tz ?? def.tz ?? tz} value={startValue} placeholder={placeholder ? t('minPrefix', { label: placeholder}) : placeholder} minDate={args?.minDate ?? def?.minDate ?? minDate} maxDate={endValue ?? args?.maxDate ?? def?.maxDate ?? maxDate} href={href ?? def.href} onChange={(v) => onChange({start: v, end: value?.end})} {...props}></TimeInput></Col>
        <Col md><TimeInput tz={args?.tz ?? def.tz ?? tz} value={endValue} placeholder={placeholder ? t('maxPrefix', { label: placeholder}) : placeholder} minDate={startValue ?? args?.minDate ?? def?.minDate ?? minDate} maxDate={args?.maxDate ?? def?.maxDate ?? maxDate} href={href ?? def.href} onChange={(v) => onChange({start: value?.start, end: v})}{...props}></TimeInput></Col>
    </Row>
}

export const customCtrlDef = {
    toString: async ({ value, args, tz, defaultValue, def, clientConfig }) => {
        value = await materializeValue(value);
        if (!value) {
            return defaultValue;
        }
        return cdate(value, args?.tz ?? def?.tz ?? tz ?? clientConfig?.tz).timeFormat(def?.showTz ?? clientConfig?.timezones?.enable, true);
    },
    def: (t, tools) => ({
        form: [{
            borderless: true,
            rows: [
                ...tools.labelFields(t),
                ...tools.fieldFields(t),
                ...tools.commonFields(t)
            ]
        }]
    }),
    control: ({ def, minDate, maxDate, tz, href, context: { args } = {}, ...props }) => <TimeInput def={args?.tz ?? def.tz ?? tz} minDate={args?.minDate ?? def?.minDate ?? minDate} maxDate={args?.maxDate ?? def?.maxDate ?? maxDate} href={href ?? def.href} {...props}></TimeInput>,
    queryControl: ({...props}, {args}) => {
        return <QueryControl args={args} {...props}></QueryControl>
    },
    comparator: (query, value) => {
        query = query?.value;
        value = value?.value;
        if (query === undefined) {
            return true;
        } else if (typeof query === 'string' && typeof value === 'string') {
            return query === value;
        } else if (typeof query === 'object' && typeof value == 'string') {
            let result = true;
            if (query.start) {
                result = result && value && parseDate(query.start) <= parseDate(value)
            }
            if (query.end) {
                result = result && value && parseDate(query.end) > parseDate(value);
            }
            return result;
        }
        return false;
    }
}

const CustomInput = forwardRef(({ value, dateFormat, required, onBlur, onChange, ...props}, parentRef) => {
    const { t } = useTranslation('common')
    const [validity, setValidity] = useState();
    const ref = useRef();
    const initialValue = useRef(value);
    const [dirty, setDirty] = useState(false);
    const [checkValue, setCheckValue] = useState(value);

    useImperativeHandle(parentRef, () => ref.current);

    useEffect(() => {
        setValidity();
        setCheckValue(_ => {
            if (value !== initialValue.current) {
                setDirty(true);
            } else {
                setDirty(false);
            }
            return value;
        })
    }, [value]);

    useEffect(() => {
        if (dirty) {
            const date = moment(checkValue, dateFormat.map(fmt => fmt.replace('aa', 'A')), true);
            setValidity(oldValid => {
                if (checkValue == '') {
                    ref.current.setCustomValidity('');
                    ref.current.checkValidity();
                    return !required;
                } else {
                    const newValid = date.isValid();
                    ref.current.setCustomValidity(newValid ? '' : t('validationTimeInvalid'))
                    ref.current.checkValidity();
                    return newValid;
                }
            });
        } else {
            setValidity();
        }
    }, [checkValue, t, dateFormat, required, dirty])

    return (
        <Validator value={value} ref={ref} onValidation={setValidity}>
            <FormControl isInvalid={validity === false} value={value} type='text' required={required} onChange={onChange} onBlur={(...props) => { onChange(...props); onBlur(...props); }} {...props} ref={ref} onInvalid={() => setValidity(false)}></FormControl>
        </Validator>
    )
})
CustomInput.propTypes = {
    value: PropTypes.string,
    dateFormat: PropTypes.any,
    required: PropTypes.bool
}

/**
 * An input tailored to displaying and accepting time values.
 * 
 * This control standardizes on its input value being an ISO timestamp string and its
 * output via the onChange function is also an ISO timestamp string.
 * @component
 */
const TimeInput = withComponentCapabilities(({value, tz, hideLabel = false, placeholder, disabled = false, required = false, readonly = false, onChange, maxDate, minDate, timeIntervals, href }) => {
    const clientConfig = useClientConfig();
    const { i18n } = useTranslation();
    const timeFormats = useMemo(() => i18n.resolvedLanguage === 'en' ? ['HH:mm', 'HHmm', 'H:mm', 'Hmm', 'h:mm aa', 'hmm aa'] : undefined, [i18n]);
    const [ guid ] = useState(crypto.randomUUID());
    
    // DatePickers only work in local tz, so we have to convert the times to counteract this
    const displayValue = useMemo(() => !value || value instanceof Date ? value : moment.tz(value, tz ?? clientConfig.tz).tz(Intl.DateTimeFormat().resolvedOptions().timeZone, true).toDate(), [clientConfig.tz, tz, value]);
    const formattedMinDate = useMemo(() => !minDate || minDate instanceof Date ? minDate : moment.tz(minDate, tz ?? clientConfig.tz).tz(Intl.DateTimeFormat().resolvedOptions().timeZone, true).toDate(), [clientConfig.tz, minDate, tz]);
    const formattedMaxDate = useMemo(() => !maxDate || maxDate instanceof Date ? maxDate : moment.tz(maxDate, tz ?? clientConfig.tz).tz(Intl.DateTimeFormat().resolvedOptions().timeZone, true).toDate(), [clientConfig.tz, maxDate, tz]);
    
    const handleChange = newValue => {
        let date = parseDate(newValue);
        if (maxDate && date > parseDate(maxDate)) {
            date = maxDate; 
        } else if (minDate && date < parseDate(minDate)) {
            date = minDate;
        }
        date = cdate(newValue)?.tz(tz ?? clientConfig.tz, true);
        const iso = date?.isValid() ? date.toISOString() : null;
        if (newValue !== iso) {
            onChange(iso);
        }
    }

    if (readonly) {
        const format = i18n.resolvedLanguage === 'en' ? 'HH:mm' : 'LT';
        return <Label placeholder={placeholder} hideLabel={hideLabel} text={!value ? '\u00A0' : moment.tz(value, tz ?? clientConfig.tz).format(format)} href={href}></Label>;
    }

    const filterTimeValues = (value) => {
        if (!minDate && !maxDate) return true;
        
        const time = new Date(value);
        if (minDate) {
            return time > formattedMinDate
        }
        if (maxDate) {
            return time < formattedMaxDate
        }
    }    

    return <FloatingLabel hideLabel={hideLabel} placeholder={placeholder} required={required} value={displayValue} href={href}><DatePicker
        customInput={<CustomInput dateFormat={timeFormats}></CustomInput>}
        selected={displayValue}
        placeholderText={makeLabel(placeholder, required)}
        disabled={disabled}
        required={required}
        showTimeSelect
        showTimeSelectOnly
        timeIntervals={timeIntervals ?? 5}
        className="form-control"
        portalId={guid}
        dateFormat={timeFormats}
        timeFormat={i18n.resolvedLanguage === 'en' ? 'HH:mm' : undefined}
        filterTime={filterTimeValues}
        strictParsing
        locale={i18n.resolvedLanguage}
        onChange={handleChange}
    ></DatePicker></FloatingLabel>
}, {

});

TimeInput.propTypes = {
    /**
     * The date to be displayed by the control as an ISO string
     */
    value: PropTypes.string,

    /**
     * The timezone in which to render the date, defaults to clientConfig.tz.
     * Timezone is expressed as a TZ identifier like Australia/Brisbane or Europe/London
     */
    tz: PropTypes.string,

    /**
     * Function to call when the value has been changed
     */
    onChange: PropTypes.func,

    /**
     * The label for the control
     */
    placeholder: PropTypes.string,

    /**
     * The control must have a value for the from in which it is contained to validate
     */
    required: PropTypes.bool,

    /**
     * Force the input to be read-only
     */
    disabled: PropTypes.bool,

    /**
     * Collapses the floating label used for spacing
     */
    hideLabel: PropTypes.bool,

     /**
     * Render the control value as text rather an input control
     */
     readonly: PropTypes.bool,
     /**
     * The minimum date to be selectable by the control as an ISO string
     */
    minDate: PropTypes.string,

    /**
     * The maximun date to be selectable by the control as an ISO string
     */
    maxDate: PropTypes.string,

    /**
     * An option navigation URL to be followed if the label text is clicked
     */
    href: PropTypes.string,
}

export default TimeInput;